import { Theme } from '@emotion/react';
import { AddCircleOutline } from '@mui/icons-material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { Box, Chip, Divider, IconButton, SxProps, Tooltip, Typography } from '@mui/material';
import ClientAvatar from 'components/shared/ClientAvatar';
import CopyableTextMUI from 'components/shared/CopyableText/CopyableText';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';
import { relationshipColorMap } from '../../data';
import { ContactType } from '../../types';
import { useSaveToMyContacts } from '../../useSaveToMyContacts';
import { muiStyles } from './muiStyles';
import styles from './styles.module.css';

const Card = ({
  contact,
  fetchContacts,
}: {
  contact: ContactType;
  fetchContacts: () => Promise<void>;
}) => {
  const { id } = useParams();
  const {
    name,
    contactRelationship,
    phone,
    email,
    lastContacted,
    profilePicture,
    roles,
    zoominfoPersonId,
    isSaved: savedStatus,
  } = contact;
  const { postContact, deleteContact } = useSaveToMyContacts();
  const [isSaved, setIsSaved] = useState(savedStatus);
  const tooltipMessage = !isSaved ? 'Add to My Contacts' : 'Remove from My Contacts';
  const Icon = !isSaved ? AddCircleOutline : CancelOutlinedIcon;

  const handleContactStatus = async () => {
    try {
      if (!isSaved) {
        await postContact(id ?? '', zoominfoPersonId);
        setIsSaved((prev) => !prev);
      } else {
        await deleteContact(id ?? '');
        setIsSaved((prev) => !prev);
      }
      await fetchContacts();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box sx={muiStyles.container}>
      <Box sx={muiStyles.header}>
        <ClientAvatar
          profilePicture={profilePicture}
          clientName={name}
          sx={muiStyles.clientAvatar}
        />
        <Box sx={muiStyles.headerContent}>
          <Box sx={muiStyles.nameAndRelationship}>
            <Typography variant='responsiveHeading4' sx={muiStyles.name}>
              {name}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <Chip
                label={
                  <Typography variant='responsiveLabel1' sx={muiStyles.chipLabel}>
                    {Utilities.formatSnakeToTitle(contactRelationship)}
                  </Typography>
                }
                sx={[relationshipColorMap[contactRelationship], muiStyles.chip] as SxProps<Theme>}
              />
              <Tooltip title={tooltipMessage}>
                <IconButton
                  size='small'
                  sx={{
                    color: zeplinColor.BasicBlack,
                  }}
                  onClick={handleContactStatus}
                >
                  <Icon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Typography variant='responsiveBody3' sx={muiStyles.title}>
            {roles}
          </Typography>
        </Box>
      </Box>

      <Box sx={muiStyles.infoRow}>
        <img src='/assets/phone-icon.svg' alt='Phone logo' className={styles.image} />
        {!phone ? (
          <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
            {'N/A'}
          </Typography>
        ) : (
          <CopyableTextMUI textToCopy={phone} />
        )}
      </Box>
      <Box sx={muiStyles.infoRow}>
        <img src='/assets/mail-dark-icon.svg' alt='Email logo' className={styles.image} />
        <Typography
          component='a'
          href={email ? `mailto:${email}` : 'N/A'}
          variant='responsiveBody3'
          sx={{ ...muiStyles.email, cursor: email ? 'pointer' : 'default' }}
        >
          {email ? email : 'N/A'}
        </Typography>
      </Box>

      <Divider component='div' sx={muiStyles.divider} />

      <Box sx={muiStyles.infoRow}>
        <img src='/assets/clock-01.svg' alt='Contact' className={styles.image} />
        <Typography variant='responsiveBody3'>
          Last Contacted: {contact?.lastContacted ? Utilities.formatDate(lastContacted) : '-'}
        </Typography>
      </Box>
    </Box>
  );
};

export default Card;
